import { useEffect, useMemo, useState } from 'react'
import { Plus, Search, Trash2 } from 'lucide-react'
import { getRecipe, listProductionProducts, saveRecipe } from '../../api/production'
import Button from '../../components/Button'
import ErrorBanner from '../../components/ErrorBanner'
import HelpTip from '../../components/HelpTip'
import Modal from '../../components/Modal'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader, FilterChips } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { money } from '../../utils/format'
import { Field } from '../master/masterShared'
import { qty } from './inventoryShared'
import { KIND_LABEL } from './productionShared'
import { ProductionGuide } from './ProductionGuide'
import { CreateOption } from '../../components/CreateOption'

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'made', label: 'Made here' },
  { id: 'material', label: 'Materials' },
  { id: 'goods', label: 'Bought to resell' },
]

// What you make: each stock product is bought to resell, a material, or
// made here from materials (its recipe). Changing what a product is moves
// its stock to the matching stock account.
export default function ProductionProducts() {
  const { notify } = useToast()
  const [rows, setRows] = useState(null)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(null)

  const load = () => listProductionProducts().then((r) => setRows(Array.isArray(r) ? r : [])).catch((e) => { setRows([]); setError(e.message || 'Could not load products.') })
  useEffect(() => { load() }, [])
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.id, (rows || []).filter((r) => f.id === 'all' || r.stock_kind === f.id).length])), [rows])
  const term = search.trim().toLowerCase()
  const shown = (rows || []).filter((r) => (filter === 'all' || r.stock_kind === filter) && (!term || `${r.name} ${r.sku}`.toLowerCase().includes(term)))

  const setKind = async (p, kind) => {
    if (kind === 'made') { setEditing({ ...p, stock_kind: 'made' }); return }
    setSaving(p.product_id)
    setError('')
    try {
      const res = await saveRecipe(p.product_id, { stock_kind: kind, components: [] })
      notify(`${p.name}: ${KIND_LABEL[kind].toLowerCase()}${res.moved ? ` (stock of ${money(res.moved)} moved)` : ''}`)
      await load()
    } catch (e) {
      setError(e.message || 'Could not change it.')
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="ci-form-page">
      <DocHeader back={<BackLink to="/inventory/production" label="Production" />} title="What you make"
        meta="Mark your materials and what you make. A recipe (what one unit uses) is optional: with one, a run takes the materials out of stock by itself." />
      <ProductionGuide />
      {error && <ErrorBanner message={error} />}
      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search products">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or SKU…" />
        </label>
      </div>
      <FilterChips options={FILTERS.map((f) => ({ ...f, count: counts[f.id] }))} value={filter} onChange={setFilter} />
      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead>
              <tr>
                <th>Product</th>
                <th>What is it? <HelpTip text="Bought to resell: stock you buy and sell as it is. Material: used to make other products (fabric, flour, wood). Made here: made from materials in a production run." /></th>
                <th className="num hide-sm">In stock</th>
                <th>Materials</th>
              </tr>
            </thead>
            <tbody>
              {rows == null ? (
                <tr><td colSpan="4"><Spinner label="Loading products…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="4"><div className="ci-empty">No products match.</div></td></tr>
              ) : shown.map((p) => (
                <tr key={p.product_id}>
                  <td><strong>{p.name}</strong>{p.sku && <span className="cell-sub">{p.sku}</span>}</td>
                  <td>
                    <select className="ci-select" value={p.stock_kind} disabled={saving === p.product_id} onChange={(e) => setKind(p, e.target.value)} aria-label={`What is ${p.name}?`}>
                      {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </td>
                  <td className="num hide-sm">{qty(p.qty_on_hand)}</td>
                  <td>
                    {p.stock_kind === 'made'
                      ? <button type="button" className="msr-link" onClick={() => setEditing(p)}>{p.components ? `${p.components} ${p.components === 1 ? 'material' : 'materials'} · Edit` : 'Add materials'}</button>
                      : <span className="sum-muted">–</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {editing && <RecipeEditor product={editing} products={rows || []} onClose={() => setEditing(null)}
        onSaved={async (res) => { setEditing(null); notify(`${editing.name}: materials saved${res.moved ? ` (stock of ${money(res.moved)} moved)` : ''}`); await load() }} />}
    </div>
  )
}

function RecipeEditor({ product, products, onClose, onSaved }) {
  const [lines, setLines] = useState(null)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    getRecipe(product.product_id).then((r) => setLines(r.components.length ? r.components.map((c) => ({ component_id: String(c.component_id), qty_per_unit: String(c.qty_per_unit) })) : [{ component_id: '', qty_per_unit: '' }]))
      .catch(() => setLines([{ component_id: '', qty_per_unit: '' }]))
  }, [product.product_id])
  const choices = products.filter((p) => p.product_id !== product.product_id)
  const setLine = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)))
  const est = (lines || []).reduce((t, l) => {
    const p = choices.find((c) => String(c.product_id) === l.component_id)
    return t + (p ? p.avg_cost * Number(l.qty_per_unit || 0) : 0)
  }, 0)

  const save = async () => {
    setSaving(true)
    setErrors({})
    try {
      onSaved(await saveRecipe(product.product_id, { stock_kind: 'made', components: lines.map((l) => ({ component_id: Number(l.component_id) || 0, qty_per_unit: l.qty_per_unit })) }))
    } catch (e) {
      setErrors(e.fields || { general: e.message || 'Could not save the materials.' })
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      size="lg"
      onClose={onClose}
      title={`${product.name}: materials`}
      subtitle="What one unit uses. A production run multiplies these by how many it makes."
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving} disabled={!lines}>Save materials</Button>
      </>}
    >
      {errors.general && <ErrorBanner message={errors.general} />}
      {!lines ? <Spinner label="Loading…" /> : (
        <>
          <table className="ci-table msr-table">
            <thead><tr><th>Material</th><th className="num">Per unit</th><th aria-label="Remove" /></tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    <select value={l.component_id} onChange={(e) => setLine(i, { component_id: e.target.value })} aria-label={`Material ${i + 1}`}>
                      <option value="">Choose…</option>
                      <CreateOption kind="product" />
                      {choices.map((c) => <option key={c.product_id} value={c.product_id}>{c.name}{c.stock_kind !== 'material' ? ` (${KIND_LABEL[c.stock_kind].toLowerCase()})` : ''}</option>)}
                    </select>
                    {errors[`components[${i}].component_id`] && <span className="acc-field-error">{errors[`components[${i}].component_id`]}</span>}
                  </td>
                  <td className="num">
                    <input inputMode="decimal" value={l.qty_per_unit} onChange={(e) => setLine(i, { qty_per_unit: e.target.value })} placeholder="0" aria-label={`Material ${i + 1} per unit`} />
                    {errors[`components[${i}].qty_per_unit`] && <span className="acc-field-error">{errors[`components[${i}].qty_per_unit`]}</span>}
                  </td>
                  <td><button type="button" className="ci-form-del-btn" aria-label={`Remove material ${i + 1}`} onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((_, j) => j !== i) : [{ component_id: '', qty_per_unit: '' }]))}><Trash2 size={15} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setLines((ls) => [...ls, { component_id: '', qty_per_unit: '' }])}><Plus size={14} />Add a material</button>
          <p className="doc-hint">Materials for one unit, at today’s costs: about {money(est)}. Labour and other costs are added on each run.</p>
          {product.stock_kind !== 'made' || product.components === 0 ? <p className="doc-hint">Saving marks {product.name} as made here.{product.qty_on_hand > 0 ? ' Its stock moves to Finished goods.' : ''}</p> : null}
        </>
      )}
    </Modal>
  )
}
